<template>

    <div>

        <rkm-form
            v-if="model"
            ref="RkmForm"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="10">
                    <rkm-form-input
                        :validations="validations.nome"
                        label="Nome"
                        v-model="model.nome"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-select
                        :validations="validations.ativo"
                        label="Status"
                        show-default-option
                        v-model="model.ativo"
                    >
                        <rkm-form-select-option :value="true">Ativo</rkm-form-select-option>
                        <rkm-form-select-option :value="false">Inativo</rkm-form-select-option>
                    </rkm-form-select>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="12">
                    <rkm-form-input
                        :validations="validations.identificadorExterno"
                        label="Identificador Externo"
                        v-model="model.identificadorExterno"
                        @input="handleIdentificadorInput"
                        maxlength="64"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="12">
                    <rkm-form-textarea
                        :validations="validations.observacao"
                        label="Observações"
                        v-model="model.observacao"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-form
            v-if="model"
            ref="RkmFormProcedimento"
        >

            <rkm-row>
                <rkm-col cols="12">
                    <rkm-area
                        title="Procedimentos"
                        type="fieldset"
                        no-margin-top
                        class="mt-0"
                    >
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-input-autocomplete
                                    :repository="repositories.procedimento"
                                    :request-query-string-params="{
                                        search_by_apelido: searchByApelido,
                                    }"
                                    :search-fields="fields.procedimento"
                                    :text-field="(item) => item.codigo ? `${formatProcedimento(item.codigo)} - ${item.nome}` : null"
                                    label="Procedimento"
                                    v-model="model.procedimento"
                                    :validations="validations.procedimento"
                                >
                                    <rkm-input-group-append slot="append">
                                        <rkm-input-group-text>
                                            <rkm-form-radio-group v-model="searchByApelido">
                                                <rkm-form-radio :value="false">Código / Nome</rkm-form-radio>
                                                <rkm-form-radio :value="true">Apelido</rkm-form-radio>
                                            </rkm-form-radio-group>
                                        </rkm-input-group-text>
                                    </rkm-input-group-append>
                                </rkm-form-input-autocomplete>
                            </rkm-col>

                            <rkm-col cols="4">
                                <rkm-form-input-autocomplete
                                    :disabled="!model.procedimento"
                                    :repository="repositories.cid"
                                    :request-query-string-params="requestQueryStringParams.cid"
                                    :search-fields="fields.cid"
                                    :validations="validations.cid"
                                    label="CID"
                                    :text-field="c => `${formatCid(c.codigo)} - ${c.nome}`"
                                    v-model="model.cid"
                                >
                                    <template #itemLabel="{ item }">
                                        {{ item.codigo | formatCid }} - {{ item.nome }}
                                    </template>
                                </rkm-form-input-autocomplete>
                            </rkm-col>

                            <rkm-col cols="2">
                                <rkm-form-input
                                    label="Qtd."
                                    v-model="model.quantidade"
                                    type="number"
                                    min="1"
                                    :validations="validations.quantidade"
                                />
                            </rkm-col>

                            <rkm-col cols="auto">
                                <rkm-button
                                    :label="false"
                                    @click="$refs.RkmFormProcedimento.validate(add)"
                                    color="primary"
                                >
                                    Incluir
                                </rkm-button>

                                <rkm-button
                                    :label="false"
                                    @click="clearProcedimento"
                                >
                                    Limpar
                                </rkm-button>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>

                                <rkm-table
                                    :fields="fields.procedimentos"
                                    :items="model.procedimentos"
                                    hide-filters
                                    hide-pagination
                                >
                                    <template v-slot:cell(procedimento)="{item}">
                                        {{`${formatProcedimento(item.codigo)} - ${item.nome}`}}
                                    </template>

                                    <template v-slot:cell(cid)="{item}">
                                        <span v-if="item.cid">{{ item.cid.codigo | formatCid }} - {{ item.cid.nome }}</span>
                                        <span v-else>-</span>
                                    </template>

                                    <template v-slot:cell(quantidade)="{item}">
                                        {{ item.quantidade || 1 }}
                                    </template>

                                    <template v-slot:cell(actions)="{index}">

                                        <rkm-button
                                            @click="destroy(index)"
                                            color="danger"
                                            small
                                            title="Excluir"
                                        >
                                            <rkm-icon icon="trash" />
                                        </rkm-button>
                                    </template>
                                </rkm-table>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>
                </rkm-col>
            </rkm-row>
        </rkm-form>
    </div>
</template>

<script>

// Mixins.
import {error, formats, repository, stringCase} from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        fields() {
            return {
                cid: [
                    { key: 'codigo', searchable: true },
                    { key: 'nome', searchable: true },
                ],
                procedimento: this.searchByApelido
                    ? [{key: 'parametrizacao_procedimento.apelido', searchable: true}]
                    : [{key: 'codigo', searchable: true}, {key: 'nome', searchable: true}],
                procedimentos: [
                    {key: 'procedimento', label: 'Procedimento', sortable: true},
                    {key: 'cid', label: 'CID', sortable: true},
                    {key: 'quantidade', label: 'Qtd', sortable: true},
                    {class: 'col-min text-center', key: 'actions', label: 'Ações'},
                ],
            }
        },
        requestQueryStringParams() {
            return {
                cid: this.model.procedimento && this.model.procedimento.hasCidPrincipal
                    ? {
                        principal: true,
                        procedimento_id: this.model.procedimento.id,
                    }
                    : {}
            }
        },
        validations() {
            return {
                nome: {
                    required: true,
                    min: 3,
                    max: 100,
                },
                ativo: {required: true},
                identificadorExterno: {max: 64},
                cid: {required: this.model.procedimento && !!this.model.procedimento.hasCidPrincipal},
                observacao: {max: 500},
                procedimento: {required: true},
                quantidade: {required: !!this.model.cid, min_value: 1},
            }
        },
    },
    created() {
        this.repositories = {
            ferida: this.getConectaSusRepository('cadastro.ferida'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }
    },
    data: () => ({
        form: null,
        model: null,
        searchByApelido: false,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        create() {
            this.repositories.ferida.create()
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                    this.$set(this.model, 'quantidade', 1)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        store(request) {
            this.repositories.ferida.store(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.showSaveNotification()
                    this.$emit('saved', response)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        edit(id) {
            this.repositories.ferida.edit(id)
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                    this.$set(this.model, 'quantidade', 1)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        update(id, request) {
            this.repositories.ferida
                .update(id, this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.showSaveNotification()
                    this.$emit('saved', response)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        load() {
            this.setLoading()

            if (this.id) {
                this.edit(this.id)
            } else {
                this.create()
            }
        },
        save() {

            if (this.model.procedimentos.length === 0) {

                this.showAlert('Cadastre ao menos um procedimento para salvar.', 'Atenção')
                return
            }

            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    this.setLoading()

                    const request = {
                        nome: this.model.nome,
                        ativo: this.model.ativo,
                        identificador_externo: this.model.identificadorExterno,
                        observacao: this.model.observacao,
                        procedimentos: this.model.procedimentos.map(p => ({
                            id: p.id,
                            cid_id: p.cid ? p.cid.id : (p.cid_id || null),
                            quantidade: p.quantidade
                        }))
                    }

                    if (this.id) {
                        this.update(this.id, request)
                    } else {
                        this.store(request)
                    }
                })
        },
        add() {
            this.model.procedimentos.push({
                id: this.model.procedimento.id,
                codigo: this.model.procedimento.codigo,
                nome: this.model.procedimento.nome,
                cid: this.model.cid ? Object.assign({}, this.model.cid) : null,
                quantidade: this.model.quantidade,
            })

            this.clearProcedimento()
        },
        clearProcedimento() {

            this.model.procedimento = null
            this.model.cid = null
            this.model.quantidade = 1
            this.$nextTick().then(() => this.$refs.RkmFormProcedimento.resetValidator())
        },
        destroy(index) {
            this.showDeleteConfirmation('Deseja realmente excluir o procedimento?', async () => {
                this.model.procedimentos.splice(index, 1)
            })
        },
        handleIdentificadorInput(value) {
            this.model.identificador_externo = value.replace(/[^a-zA-Z0-9]/g, '')
        },
    },
    mixins: [error, formats, repository, stringCase],
    mounted() {
        this.load()
    },
    props: {
        id: {type: [Number, String], default: null},
    },
}
</script>